import { Controller } from "@hotwired/stimulus"
import { createLogger } from '../utils/logger'

export default class extends Controller {
  static targets = [
    "treeContainer",
    "searchInput",
    "selectedElement",
    "detailsContainer",
    "stylesPanel",
    "computedPanel",
    "layoutPanel",
    "propertiesPanel",
    "eventsPanel"
  ]

  static values = {
    buildSessionId: String,
    selectedElementPath: String,
    domTree: { type: Object, default: {} },
    expandedNodes: { type: Array, default: [] }
  }

  connect() {
    this.logger = createLogger('dom-inspector')
    this.setupEventListeners()
    // Listen for DOM data from the parent preview controller
    document.addEventListener('preview-devtools:dom-data-received', this.handleNewDomData.bind(this))
    this.captureInitialDOM()
  }

  disconnect() {
    this.teardownEventListeners()
    document.removeEventListener('preview-devtools:dom-data-received', this.handleNewDomData.bind(this))
  }

  setupEventListeners() {
    // Listen for new postMessage-based events
    this.element.addEventListener('preview-devtools:dom-data-received', this.handleNewDomData.bind(this))
    
    // Keep legacy event listeners for compatibility
    this.element.addEventListener('preview-devtools:dom-message', this.handleDomMessage.bind(this))
    this.element.addEventListener('preview-devtools:clear-dom', this.clearDOM.bind(this))
    this.element.addEventListener('preview-devtools:export-dom', this.exportDOM.bind(this))
  }

  teardownEventListeners() {
    this.element.removeEventListener('preview-devtools:dom-data-received', this.handleNewDomData.bind(this))
    this.element.removeEventListener('preview-devtools:dom-message', this.handleDomMessage.bind(this))
    this.element.removeEventListener('preview-devtools:clear-dom', this.clearDOM.bind(this))
    this.element.removeEventListener('preview-devtools:export-dom', this.exportDOM.bind(this))
  }

  handleNewDomData(event) {
    this.logger.debug('Received new DOM data', event.detail)
    const domData = event.detail
    
    // Convert the new format to the expected structure
    const processedData = this.processDOMData(domData)
    this.updateDOMTree(processedData)
  }

  processDOMData(rawData) {
    // The new format provides HTML, URL, title, elements summary
    // We need to parse the HTML into our tree structure
    if (rawData.html) {
      try {
        // Create a temporary DOM parser
        const parser = new DOMParser()
        const doc = parser.parseFromString(rawData.html, 'text/html')
        
        // Convert to our tree structure
        const processedTree = this.convertElementToTree(doc.documentElement)
        return processedTree
      } catch (error) {
        this.logger.error('Failed to parse HTML', error)
        return this.createEmptyDOMData(rawData)
      }
    }
    
    return this.createEmptyDOMData(rawData)
  }

  convertElementToTree(element) {
    const node = {
      tag: element.tagName.toLowerCase(),
      attributes: {},
      children: [],
      text: null
    }
    
    // Process attributes
    if (element.attributes) {
      for (let i = 0; i < element.attributes.length; i++) {
        const attr = element.attributes[i]
        node.attributes[attr.name] = attr.value
      }
    }
    
    // Process child nodes
    const childElements = Array.from(element.children)
    const textNodes = Array.from(element.childNodes).filter(child => child.nodeType === Node.TEXT_NODE)
    
    // Get text content
    if (textNodes.length > 0) {
      const text = textNodes.map(n => n.textContent).join('').trim()
      if (text && text.length > 0) {
        node.text = text
      }
    }
    
    // Process child elements (limit to prevent performance issues)
    childElements.slice(0, 100).forEach(child => {
      const childNode = this.convertElementToTree(child)
      node.children.push(childNode)
    })
    
    return node
  }

  createEmptyDOMData(rawData) {
    return {
      tag: 'div',
      attributes: { class: 'empty-dom' },
      children: [],
      text: `DOM data received but could not be parsed. URL: ${rawData.url || 'unknown'}`
    }
  }

  handleDomMessage(event) {
    const { type, data } = event.detail
    
    switch (type) {
      case 'dom-snapshot':
        this.updateDOMTree(data)
        break
      case 'element-selected':
        this.selectElementByPath(data.path)
        break
      case 'element-highlighted':
        this.handleElementHighlighted(data)
        break
    }
  }

  captureInitialDOM() {
    this.captureDOM()
  }

  captureDOM() {
    this.logger.debug('Attempting to capture DOM...')
    const iframe = document.querySelector('#preview-iframe')
    if (!iframe || !iframe.contentWindow) {
      console.error('DOM Inspector: No iframe or contentWindow found')
      return
    }
    
    // Request DOM snapshot from iframe
    console.log('DOM Inspector: Sending capture-dom message to iframe')
    iframe.contentWindow.postMessage({
      type: 'capture-dom'
    }, '*')
    
    // Set a timeout to check if we got a response
    setTimeout(() => {
      if (!this.domTreeValue || Object.keys(this.domTreeValue).length === 0) {
        console.warn('DOM Inspector: No DOM data received after 3 seconds')
      }
    }, 3000)
  }

  refreshDOM() {
    this.captureDOM()
  }

  updateDOMTree(domData) {
    this.domTreeValue = domData
    this.renderDOMTree()
  }

  renderDOMTree() {
    if (!this.domTreeValue || Object.keys(this.domTreeValue).length === 0) {
      this.treeContainerTarget.innerHTML = `
        <div class="flex items-center justify-center h-full text-base-content/40">
          <div class="text-center">
            <i class="fas fa-code text-2xl mb-2"></i>
            <p>DOM tree will appear here</p>
            <button class="btn btn-xs btn-primary mt-2" data-action="click->dom-inspector#captureDOM">
              Capture DOM
            </button>
          </div>
        </div>
      `
      return
    }
    
    this.treeContainerTarget.innerHTML = ''
    const rootElement = this.createDOMNode(this.domTreeValue, '', 0)
    this.treeContainerTarget.appendChild(rootElement)
  }

  createDOMNode(node, path, level) {
    const div = document.createElement('div')
    div.className = 'dom-node hover:bg-base-200/50 py-0.5'
    div.dataset.path = path
    div.dataset.level = level
    
    // Indentation
    const indent = document.createElement('span')
    indent.style.width = `${level * 20}px`
    indent.className = 'inline-block'
    div.appendChild(indent)
    
    // Expand/collapse arrow
    if (node.children && node.children.length > 0) {
      const arrow = document.createElement('i')
      arrow.className = 'fas fa-caret-right text-xs cursor-pointer mr-1 transition-transform'
      arrow.dataset.action = 'click->dom-inspector#toggleNode'
      
      if (this.expandedNodesValue.includes(path)) {
        arrow.classList.add('rotate-90')
      }
      
      div.appendChild(arrow)
    } else {
      const spacer = document.createElement('span')
      spacer.className = 'inline-block w-4'
      div.appendChild(spacer)
    }
    
    // Element tag
    const tagOpen = document.createElement('span')
    tagOpen.className = 'text-primary cursor-pointer'
    tagOpen.dataset.action = 'click->dom-inspector#selectNode'
    tagOpen.innerHTML = `&lt;${node.tag}`
    div.appendChild(tagOpen)
    
    // Attributes
    if (node.attributes) {
      Object.entries(node.attributes).forEach(([key, value]) => {
        const attr = document.createElement('span')
        attr.className = 'text-info ml-1'
        attr.textContent = `${key}="${value}"`
        div.appendChild(attr)
      })
    }
    
    const tagClose = document.createElement('span')
    tagClose.className = 'text-primary'
    tagClose.innerHTML = '&gt;'
    div.appendChild(tagClose)
    
    // Text content
    if (node.text && node.text.trim()) {
      const text = document.createElement('span')
      text.className = 'text-base-content/70 ml-1'
      text.textContent = node.text.trim().substring(0, 50)
      if (node.text.trim().length > 50) {
        text.textContent += '...'
      }
      div.appendChild(text)
    }
    
    // Closing tag
    if (node.children && node.children.length > 0) {
      const closingTag = document.createElement('span')
      closingTag.className = 'text-primary ml-1'
      closingTag.innerHTML = `&lt;/${node.tag}&gt;`
      div.appendChild(closingTag)
    }
    
    // Container for children
    const container = document.createElement('div')
    container.appendChild(div)
    
    // Render children if expanded
    if (node.children && this.expandedNodesValue.includes(path)) {
      const childrenContainer = document.createElement('div')
      childrenContainer.className = 'dom-children'
      
      node.children.forEach((child, index) => {
        const childPath = path ? `${path}.${index}` : `${index}`
        const childElement = this.createDOMNode(child, childPath, level + 1)
        childrenContainer.appendChild(childElement)
      })
      
      container.appendChild(childrenContainer)
    }
    
    return container
  }

  toggleNode(event) {
    event.stopPropagation()
    const node = event.target.closest('.dom-node')
    const path = node.dataset.path
    
    if (this.expandedNodesValue.includes(path)) {
      this.expandedNodesValue = this.expandedNodesValue.filter(p => p !== path)
      event.target.classList.remove('rotate-90')
    } else {
      this.expandedNodesValue = [...this.expandedNodesValue, path]
      event.target.classList.add('rotate-90')
    }
    
    this.renderDOMTree()
  }

  selectNode(event) {
    event.stopPropagation()
    const node = event.target.closest('.dom-node')
    const path = node.dataset.path
    
    this.selectElementByPath(path)
  }

  selectElementByPath(path) {
    this.selectedElementPathValue = path
    
    // Update selected element display
    const element = this.getElementByPath(path)
    if (element) {
      this.selectedElementTarget.textContent = `${element.tag}${element.attributes?.id ? `#${element.attributes.id}` : ''}${element.attributes?.class ? `.${element.attributes.class.split(' ').join('.')}` : ''}`
      
      // Load element details
      this.loadElementStyles(element)
      this.loadComputedStyles(element)
      this.loadElementLayout(element)
      this.loadElementProperties(element)
      this.loadElementEvents(element)
      
      // Highlight in iframe
      this.highlightElement()
    }
    
    // Update visual selection
    this.treeContainerTarget.querySelectorAll('.dom-node').forEach(node => {
      node.classList.toggle('bg-primary/10', node.dataset.path === path)
    })
  }

  getElementByPath(path) {
    if (!path) return this.domTreeValue
    
    const indices = path.split('.').map(i => parseInt(i))
    let element = this.domTreeValue
    
    for (const index of indices) {
      if (element.children && element.children[index]) {
        element = element.children[index]
      } else {
        return null
      }
    }
    
    return element
  }

  loadElementStyles(element) {
    const styles = element.styles || {}
    const stylesHTML = Object.entries(styles).map(([property, value]) => `
      <div class="flex justify-between py-1 hover:bg-base-200/50">
        <span class="text-info">${property}:</span>
        <span>${value}</span>
      </div>
    `).join('')
    
    this.stylesPanelTarget.innerHTML = stylesHTML || '<div class="text-base-content/40">No inline styles</div>'
  }

  loadComputedStyles(element) {
    const computed = element.computed || {}
    const computedHTML = Object.entries(computed).map(([property, value]) => `
      <div class="flex justify-between py-1 hover:bg-base-200/50">
        <span class="text-info">${property}:</span>
        <span>${value}</span>
      </div>
    `).join('')
    
    this.computedPanelTarget.innerHTML = computedHTML || '<div class="text-base-content/40">No computed styles available</div>'
  }

  loadElementLayout(element) {
    const layout = element.layout || {}
    const layoutHTML = `
      <div class="box-model bg-base-200 rounded p-4">
        <div class="text-center mb-2">Box Model</div>
        <div class="bg-warning/20 p-2 rounded">
          <div class="text-xs text-center">margin</div>
          <div class="text-center">${layout.margin || '0'}</div>
          <div class="bg-success/20 p-2 rounded mt-2">
            <div class="text-xs text-center">border</div>
            <div class="text-center">${layout.border || '0'}</div>
            <div class="bg-info/20 p-2 rounded mt-2">
              <div class="text-xs text-center">padding</div>
              <div class="text-center">${layout.padding || '0'}</div>
              <div class="bg-primary/20 p-2 rounded mt-2">
                <div class="text-center">${layout.width || 'auto'} × ${layout.height || 'auto'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
    
    this.layoutPanelTarget.innerHTML = layoutHTML
  }

  loadElementProperties(element) {
    const properties = element.properties || {}
    const propertiesHTML = Object.entries(properties).map(([key, value]) => `
      <div class="flex justify-between py-1 hover:bg-base-200/50">
        <span class="text-info">${key}:</span>
        <span>${typeof value === 'object' ? JSON.stringify(value) : value}</span>
      </div>
    `).join('')
    
    this.propertiesPanelTarget.innerHTML = propertiesHTML || '<div class="text-base-content/40">No properties available</div>'
  }

  loadElementEvents(element) {
    const events = element.events || []
    const eventsHTML = events.map(event => `
      <div class="py-1 hover:bg-base-200/50">
        <span class="badge badge-xs badge-primary">${event.type}</span>
        <span class="ml-2 text-xs">${event.handler || 'Anonymous function'}</span>
      </div>
    `).join('')
    
    this.eventsPanelTarget.innerHTML = eventsHTML || '<div class="text-base-content/40">No event listeners</div>'
  }

  highlightElement() {
    const iframe = document.querySelector('#preview-iframe')
    if (!iframe || !iframe.contentWindow) return
    
    iframe.contentWindow.postMessage({
      type: 'highlight-element',
      path: this.selectedElementPathValue
    }, '*')
  }

  copySelector() {
    const element = this.getElementByPath(this.selectedElementPathValue)
    if (!element) return
    
    let selector = element.tag
    if (element.attributes?.id) {
      selector = `#${element.attributes.id}`
    } else if (element.attributes?.class) {
      selector = `${element.tag}.${element.attributes.class.split(' ').join('.')}`
    }
    
    navigator.clipboard.writeText(selector)
    
    // Show toast notification
    const toast = document.createElement('div')
    toast.className = 'toast toast-top toast-end'
    toast.innerHTML = `
      <div class="alert alert-success">
        <span>Selector copied!</span>
      </div>
    `
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2000)
  }

  searchElements(event) {
    const searchTerm = event.target.value.toLowerCase()
    if (!searchTerm) {
      this.renderDOMTree()
      return
    }
    
    // Filter DOM tree based on search
    this.filterDOMTree(searchTerm)
  }

  filterDOMTree(searchTerm) {
    const filteredTree = this.filterNode(this.domTreeValue, searchTerm)
    if (filteredTree) {
      this.treeContainerTarget.innerHTML = ''
      const rootElement = this.createDOMNode(filteredTree, '', 0)
      this.treeContainerTarget.appendChild(rootElement)
    }
  }

  filterNode(node, searchTerm) {
    const nodeMatches = node.tag.toLowerCase().includes(searchTerm) ||
                       (node.attributes?.id && node.attributes.id.toLowerCase().includes(searchTerm)) ||
                       (node.attributes?.class && node.attributes.class.toLowerCase().includes(searchTerm))
    
    let matchingChildren = []
    if (node.children) {
      matchingChildren = node.children.map(child => this.filterNode(child, searchTerm)).filter(Boolean)
    }
    
    if (nodeMatches || matchingChildren.length > 0) {
      return {
        ...node,
        children: matchingChildren
      }
    }
    
    return null
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    
    // Update tab states
    event.currentTarget.parentElement.querySelectorAll('.tab').forEach(t => {
      t.classList.toggle('tab-active', t.dataset.tab === tab)
    })
    
    // Show/hide panels
    this.detailsContainerTarget.querySelectorAll('[data-tab-content]').forEach(panel => {
      panel.classList.toggle('hidden', panel.dataset.tabContent !== tab)
    })
  }

  clearDOM() {
    this.domTreeValue = {}
    this.selectedElementPathValue = ''
    this.expandedNodesValue = []
    this.renderDOMTree()
  }

  exportDOM() {
    const data = {
      domTree: this.domTreeValue,
      selectedElement: this.selectedElementPathValue,
      expandedNodes: this.expandedNodesValue,
      exportedAt: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dom-snapshot-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}